import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import purchaseOrderApprovalService from '@/services/purchaseOrderApproval.service';
import {
  UnapprovedPO,
  Supplier,
  AutomationConfig,
  GetUnapprovedPOsParams,
} from '@/models/unapprovedPO';

interface UnapprovedPOsState {
  purchaseOrders: UnapprovedPO[];
  loading: boolean;
  error: string | null;
  filters: {
    supplierId: number | null;
    automationConfigId: number | null;
    dateRange: [string, string] | null;
    searchQuery: string;
  };
  sorting: {
    sortBy: string;
    sortDesc: boolean;
  };
  pagination: {
    page: number;
    itemsPerPage: number;
    totalItems: number;
  };
  selection: number[];
  metadata: {
    suppliers: Supplier[];
    automationConfigs: AutomationConfig[];
  };
}

@Module({ namespaced: true, name: 'unapprovedPOs' })
class UnapprovedPOs extends VuexModule implements UnapprovedPOsState {
  public purchaseOrders: UnapprovedPO[] = [];
  public loading = false;
  public error: string | null = null;
  public filters: {
    supplierId: number | null;
    automationConfigId: number | null;
    dateRange: [string, string] | null;
    searchQuery: string;
  } = {
    supplierId: null,
    automationConfigId: null,
    dateRange: null,
    searchQuery: '',
  };
  public sorting = {
    sortBy: 'createdAt',
    sortDesc: true,
  };
  public pagination = {
    page: 1,
    itemsPerPage: 20,
    totalItems: 0,
  };
  public selection: number[] = [];
  public metadata = {
    suppliers: [] as Supplier[],
    automationConfigs: [] as AutomationConfig[],
  };

  @Mutation
  SET_FILTERS(filters: { supplierId: number | null; automationConfigId: number | null; dateRange: [string, string] | null; searchQuery: string }) {
    this.filters = filters;
  }

  @Action
  public async fetchUnapprovedPOs(params: GetUnapprovedPOsParams) {
    this.context.commit('SET_LOADING', true);
    this.context.commit('SET_ERROR', null);
    try {
      const response = await purchaseOrderApprovalService.getUnapprovedPOs(params);
      console.log('API Response:', response);

      // Handle the actual API response structure
      if (response.data && Array.isArray(response.data)) {
        this.context.commit('SET_PURCHASE_ORDERS', {
          purchaseOrders: response.data,
          totalItems: response.pagination?.total || response.data.length,
        });

        // Extract suppliers and automation configs from the data if not in meta
        if (!response.meta) {
          const suppliers = [...new Set(response.data
            .filter((po: any) => po.supplier)
            .map((po: any) => ({ id: po.supplierId, name: po.supplier.name })))];
          const automationConfigs = [...new Set(response.data
            .filter((po: any) => po.automationConfig)
            .map((po: any) => ({ id: po.automationConfigId, name: po.automationConfig.name })))];

          this.context.commit('SET_METADATA', { suppliers, automationConfigs });
        } else {
          this.context.commit('SET_METADATA', response.meta);
        }
      }
    } catch (err: any) {
      this.context.commit('SET_ERROR', err.message || 'Failed to fetch unapproved purchase orders.');
    } finally {
      this.context.commit('SET_LOADING', false);
    }
  }

  @Action
  public async bulkApprove(payload: { purchaseOrderIds: number[], notes?: string }) {
    this.context.commit('SET_LOADING', true);
    this.context.commit('SET_ERROR', null);
    try {
      await purchaseOrderApprovalService.bulkApprove({
        ids: payload.purchaseOrderIds,
        notes: payload.notes,
      });
      // After success, you might want to refresh the list
      // this.context.dispatch('fetchUnapprovedPOs', { /* original params */ });
    } catch (err: any) {
      this.context.commit('SET_ERROR', err.message || 'Failed to approve purchase orders.');
    } finally {
      this.context.commit('SET_LOADING', false);
    }
  }

  @Action
  public async bulkReject(payload: { purchaseOrderIds: number[], reason: string }) {
    this.context.commit('SET_LOADING', true);
    this.context.commit('SET_ERROR', null);
    try {
      await purchaseOrderApprovalService.bulkReject({ ids: payload.purchaseOrderIds, reason: payload.reason });
      // After success, you might want to refresh the list
      // this.context.dispatch('fetchUnapprovedPOs', { /* original params */ });
    } catch (err: any) {
      this.context.commit('SET_ERROR', err.message || 'Failed to reject purchase orders.');
    } finally {
      this.context.commit('SET_LOADING', false);
    }
  }

  @Mutation
  private SET_LOADING(isLoading: boolean) {
    this.loading = isLoading;
  }

  @Mutation
  private SET_ERROR(error: string | null) {
    this.error = error;
  }

  @Mutation
  private SET_PURCHASE_ORDERS(data: { purchaseOrders: UnapprovedPO[], totalItems: number }) {
    this.purchaseOrders = data.purchaseOrders;
    this.pagination.totalItems = data.totalItems;
  }

  @Mutation
  private SET_METADATA(data: { suppliers: Supplier[], automationConfigs: AutomationConfig[] }) {
    this.metadata.suppliers = data.suppliers;
    this.metadata.automationConfigs = data.automationConfigs;
  }

  @Mutation
  private SET_PAGINATION(pagination: { page: number, itemsPerPage: number }) {
    this.pagination.page = pagination.page;
    this.pagination.itemsPerPage = pagination.itemsPerPage;
  }

  @Mutation
  private SET_SORTING(sorting: { sortBy: string, sortDesc: boolean }) {
    this.sorting.sortBy = sorting.sortBy;
    this.sorting.sortDesc = sorting.sortDesc;
  }

  @Mutation
  private SET_SELECTION(selection: number[]) {
    this.selection = selection;
  }
}

export default UnapprovedPOs;
